import React, { useState } from 'react';
import { Share2, Copy, Check, Facebook, Twitter, MessageCircle, Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';

export default function ShareAnalysisButton({ analysisData }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [generating, setGenerating] = useState(false);

    const generateShareText = () => {
        if (!analysisData?.results) return '';
        
        const params = ['ph', 'chlorine', 'calcium', 'stabilizer'];
        const labels = { ph: 'pH', chlorine: 'Chlore', calcium: 'Calcaire', stabilizer: 'Stabilisant' };
        const statusEmoji = { ideal: '✅', low: '⚠️', high: '⚠️', critical_low: '❌', critical_high: '❌' };
        
        let text = "🏊 Analyse d'eau piscine - O.C.A Piscine\n\n";
        
        const overall = analysisData.results.overall;
        if (overall === 'good') {
            text += "✨ État général : Excellent\n\n";
        } else if (overall === 'warning') {
            text += "⚠️ État général : Attention requise\n\n";
        } else {
            text += "❌ État général : Action urgente\n\n";
        }
        
        text += "📊 Paramètres :\n";
        params.forEach(param => {
            const result = analysisData.results[param];
            if (result) {
                const emoji = statusEmoji[result.status] || '❓';
                const unit = param === 'ph' ? '' : ' ppm';
                text += `${emoji} ${labels[param]}: ${result.value}${unit}\n`;
            }
        });
        
        if (analysisData.recommendations?.length > 0) {
            text += "\n💡 Recommandations :\n";
            analysisData.recommendations.slice(0, 3).forEach(rec => {
                text += `• ${rec.product_name}\n`;
            });
        }
        
        text += "\n🔗 Analysez votre eau sur O.C.A Piscine";
        
        return text;
    };

    const shareText = generateShareText();
    const shareUrl = window.location.origin;

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareText);
            setCopied(true);
            toast.success('Résultats copiés !');
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast.error('Impossible de copier');
        }
    };

    const handleShareFacebook = () => {
        const url = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(shareText)}&u=${encodeURIComponent(shareUrl)}`;
        window.open(url, '_blank', 'width=600,height=400');
    };

    const handleShareTwitter = () => {
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText.slice(0, 280))}`;
        window.open(url, '_blank', 'width=600,height=400');
    };

    const handleShareWhatsApp = () => {
        const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
    };

    const handleDownloadPDF = async () => {
        setGenerating(true);
        try {
            // Create a simple text file as PDF generation would need a library
            const blob = new Blob([shareText], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `analyse-piscine-${new Date().toISOString().split('T')[0]}.txt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('Fichier téléchargé !');
        } catch (error) {
            toast.error('Erreur lors du téléchargement');
        } finally {
            setGenerating(false);
        }
    };

    if (!analysisData?.results) return null;

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="share-analysis-btn">
                    <Share2 className="h-4 w-4 mr-2" />
                    Partager
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Partager les résultats</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                    {/* Preview */}
                    <div className="bg-muted rounded-lg p-4 max-h-48 overflow-y-auto">
                        <pre className="text-xs whitespace-pre-wrap font-sans">
                            {shareText}
                        </pre>
                    </div>

                    {/* Share buttons */}
                    <div className="grid grid-cols-2 gap-3">
                        <Button
                            variant="outline"
                            onClick={handleCopyLink}
                            className="w-full"
                        >
                            {copied ? (
                                <>
                                    <Check className="h-4 w-4 mr-2 text-green-500" />
                                    Copié !
                                </>
                            ) : (
                                <>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Copier
                                </>
                            )}
                        </Button>
                        
                        <Button
                            variant="outline"
                            onClick={handleDownloadPDF}
                            disabled={generating}
                            className="w-full"
                        >
                            {generating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    <Download className="h-4 w-4 mr-2" />
                                    Télécharger
                                </>
                            )}
                        </Button>
                    </div>

                    <div className="flex justify-center gap-4 pt-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleShareFacebook}
                            className="rounded-full bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            <Facebook className="h-5 w-5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleShareTwitter}
                            className="rounded-full bg-sky-500 hover:bg-sky-600 text-white"
                        >
                            <Twitter className="h-5 w-5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleShareWhatsApp}
                            className="rounded-full bg-green-500 hover:bg-green-600 text-white"
                        >
                            <MessageCircle className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
